import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DrawerTitleComponent,
  Icon,
  PrimengDrawerDirective,
  ProgressBarComponent,
  UploadComponent,
  UploadEvent,
  UploadException,
} from '@web/shared/ui';
import { catchError, combineLatest, Observable, tap, throwError } from 'rxjs';
import { MultimediaChooseOrUploadDropzoneComponent } from '../../components/multimedia-choose-or-upload-dropzone/multimedia-choose-or-upload-dropzone.component';
import { Drawer } from 'primeng/drawer';
import {
  blank,
  Guid,
  isNotEmptyArray,
  isNotNil,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import { MultimediaTileComponent } from '../../../shared/components/multimedia-tile/multimedia-tile.component';
import { AdminMultimediaDataSourceService } from '../../services/admin-multimedia-data-source.service';
import { AdminFetchMultimediaByIdPipe } from '../../pipes/admin-fetch-multimedia-by-id.pipe';
import { MultimediaType } from '../../../shared/enums/multimedia-type';
import { MultimediaChooseMultipleComponent } from '../multimedia-choose-multiple/multimedia-choose-multiple.component';
import { HttpErrorService } from '@web/common';
import { MultimediaGridLayoutComponent } from '../../components/multimedia-grid-layout/multimedia-grid-layout.component';
import { MultimediaGridLayoutItemComponent } from '../../components/multimedia-grid-layout-item/multimedia-grid-layout-item.component';

@Component({
  selector: 'app-multimedia-choose-or-upload-multiple',
  imports: [
    CommonModule,
    Drawer,
    Drawer,
    DrawerTitleComponent,
    AdminFetchMultimediaByIdPipe,
    MultimediaChooseMultipleComponent,
    MultimediaChooseOrUploadDropzoneComponent,
    MultimediaGridLayoutComponent,
    MultimediaGridLayoutItemComponent,
    MultimediaTileComponent,
    PrimengDrawerDirective,
    ProgressBarComponent,
    UploadComponent,
  ],
  templateUrl: './multimedia-choose-or-upload-multiple.component.html',
  styleUrl: './multimedia-choose-or-upload-multiple.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaChooseOrUploadMultipleComponent {
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);
  private readonly httpError = inject(HttpErrorService);

  public readonly maxFileSize = input<number>(2 * 1000 * 1000);
  public readonly allowedMimeTypes = input<string>('');
  public readonly allowedMultimediaTypes = input<Nillable<MultimediaType[]>>();
  public readonly multimediaIds = input<Nillable<Guid[]>>();
  public readonly disabled = input(false);
  public readonly chooseClickEvent = output<Guid[]>();
  public readonly clearClickEvent = output<Guid[]>();

  protected readonly shouldPreview = this.shouldPreviewSignal();
  protected readonly chosenIds = signal<Guid[]>([]);
  protected readonly Icon = Icon;

  protected imageExplorerVisibility = false;

  private readonly uploadComponentRef = viewChild.required(UploadComponent);
  private readonly drawerComponentRef = viewChild.required(Drawer);

  public constructor() {
    effect(() => this.updateChosenByMultimedia());
  }

  public handleMultimediaLibraryClickEvent(): void {
    this.showImageExplorer();
  }

  public handleSendFileClickEvent(): void {
    this.uploadComponentRef().showChooseFileDialog();
  }

  public handleConfirmSelectedEvent(ids: Guid[]): void {
    this.addToChosenIds(ids);
    this.notifyChooseClickEvent();
    this.hideImageExplorer();
  }

  public handleRemoveClickEvent(id: Guid): void {
    this.removeFromChosenIds(id);
    this.notifyChooseClickEvent();
  }

  protected readonly uploadFn = (event: UploadEvent) => this.upload(event);

  protected shouldPreviewSignal(): Signal<boolean> {
    return computed(() => {
      const result = this.chosenIds();

      return isNotNil(result) && isNotEmptyArray(result);
    });
  }

  private updateChosenByMultimedia(): void {
    this.addToChosenIds(valueOrDefault(this.multimediaIds(), []));
  }

  private upload(event: UploadEvent): Observable<void> {
    const { files } = event;

    return combineLatest(
      files.map((file) =>
        this.multimediaDataSource.upload(file).pipe(
          tap({
            next: (multimedia) => {
              this.addToChosenIds([multimedia.id]);
              this.notifyChooseClickEvent();
            },
          }),
          catchError((response) =>
            throwError(
              () =>
                new UploadException(
                  this.httpError.extract(response).multiLineMessages(),
                ),
            ),
          ),
        ),
      ),
    ).pipe(blank());
  }

  private notifyChooseClickEvent(): void {
    this.chooseClickEvent.emit(this.chosenIds());
  }

  private addToChosenIds(ids: Guid[]): void {
    const onlyUniquesIds = (result: Guid[]) => (id: Guid) =>
      !result.includes(id);

    this.chosenIds.update((result) => [
      ...result,
      ...ids.filter(onlyUniquesIds(result)),
    ]);
  }

  private removeFromChosenIds(id: Guid): void {
    this.chosenIds.update((result) => result.filter((item) => item !== id));
  }

  private showImageExplorer(): void {
    this.imageExplorerVisibility = true;
  }

  private hideImageExplorer(): void {
    this.drawerComponentRef().close(this.createDrawerCloseEvent());
  }

  private createDrawerCloseEvent(): Event {
    return new Event('manual-drawer-close', { cancelable: true });
  }
}
